import { Close } from '@mui/icons-material';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AssignAgentsList from '../components/assignAgentsList';
import { useAuth } from 'contexts/ProtectedRoute';

import { AgentRoles } from 'api/models/payload/AgentRoles';
import ROLES from 'assets/userRoles.json';
import { useAgentRoleLists, useAssignAgentMutation } from 'hooks/useAssignAgents';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadApp } from 'redux/application/application.middleware';
import { loadSalesClients } from 'redux/sales-clients/sales-clients.middleware';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface AssignAgentsModalProps {
    appIds: number[];
    onClose: () => void;
    refreshMode?: 'app' | 'clients';
}

const AssignAgentsDialog = ({ appIds, onClose, refreshMode = 'app' }: AssignAgentsModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const { role } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const salesClientsList = useSelector((state: RootState) => state.salesClients.list);

    const isAdmin = role === ROLES.admin;
    const includeSales = !isAdmin;
    const includeBackoffice = !isAdmin;
    const includeConsultant = true;

    const { salesAgents, backofficeAgents, consultantAgents, isLoading, isError, error } =
        useAgentRoleLists({
            enabled: true,
            includeSales,
            includeBackoffice,
            includeConsultant,
        });

    const assignMutation = useAssignAgentMutation();
    const [assigningRole, setAssigningRole] = useState<AgentRoles | null>(null);

    useEffect(() => {
        if (!isError) return;

        dispatch(
            setAlert({
                message: getErrorMessage(error, translate('agentsLoadError')),
                type: 'error',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- translate is unstable across renders
    }, [isError, error, dispatch]);

    const currentAgentIds = useMemo(() => {
        if (refreshMode === 'clients') {
            const selected = salesClientsList.filter((client) => appIds.includes(client.id));
            if (!selected.length) {
                return {
                    sales: null as number | null,
                    backoffice: null as number | null,
                    consultant: null as number | null,
                };
            }
            const commonOrNull = (
                key: 'sales_id' | 'backoffice_id' | 'consultant_id'
            ): number | null => {
                const first = selected[0][key];
                if (first == null) return null;
                return selected.every((client) => client[key] === first) ? first : null;
            };
            return {
                sales: commonOrNull('sales_id'),
                backoffice: commonOrNull('backoffice_id'),
                consultant: commonOrNull('consultant_id'),
            };
        }
        return {
            sales: activeApp?.sales_id ?? null,
            backoffice: activeApp?.backoffice_id ?? null,
            consultant: activeApp?.consultant_id ?? null,
        };
    }, [refreshMode, appIds, salesClientsList, activeApp]);

    const sections = useMemo(
        () =>
            [
                {
                    key: 'sales',
                    title: translate('salesAgent'),
                    currentId: currentAgentIds.sales,
                    agents: salesAgents,
                    role: AgentRoles.SALES,
                    visible: includeSales,
                },
                {
                    key: 'backoffice',
                    title: translate('backofficeAgent'),
                    currentId: currentAgentIds.backoffice,
                    agents: backofficeAgents,
                    role: AgentRoles.BACKOFFICE,
                    visible: includeBackoffice,
                },
                {
                    key: 'visa',
                    title: translate('visaAgent'),
                    currentId: currentAgentIds.consultant,
                    agents: consultantAgents,
                    role: AgentRoles.CONSULTANT,
                    visible: includeConsultant,
                },
            ].filter((section) => section.visible),
        [
            translate,
            currentAgentIds,
            salesAgents,
            backofficeAgents,
            consultantAgents,
            includeSales,
            includeBackoffice,
            includeConsultant,
        ]
    );

    const handleAssign = async (agentRole: AgentRoles, agentId: number): Promise<boolean> => {
        if (!appIds.length) return false;

        setAssigningRole(agentRole);
        try {
            await assignMutation.mutateAsync({
                role: agentRole,
                agentId,
                applicationIds: appIds,
            });

            if (refreshMode === 'clients') {
                await dispatch(loadSalesClients({}));
            } else if (appIds.length === 1) {
                await dispatch(loadApp(appIds[0].toString()));
            }

            dispatch(
                setAlert({
                    message:
                        appIds.length > 1
                            ? translate('agentsAssignedSuccessfully').replace(
                                  '{{count}}',
                                  String(appIds.length)
                              )
                            : translate('agentAssignedSuccessfully'),
                    type: 'success',
                })
            );
            return true;
        } catch (assignError) {
            dispatch(
                setAlert({
                    message: getErrorMessage(assignError, translate('agentAssignmentFailed')),
                    type: 'error',
                })
            );
            return false;
        } finally {
            setAssigningRole(null);
        }
    };

    const title =
        appIds.length > 1
            ? translate('assignAgentsBulkTitle').replace('{{count}}', String(appIds.length))
            : translate('assignAgents');

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            scroll="paper"
            aria-labelledby="assign-agents-dialog-title"
        >
            <DialogTitle
                id="assign-agents-dialog-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 1.5,
                    px: { xs: 2, sm: 2.5 },
                }}
            >
                <Stack spacing={0.25} sx={{ minWidth: 0, pr: 0.5 }}>
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{ fontWeight: 700, lineHeight: 1.3 }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 400, fontSize: 13, lineHeight: 1.35 }}
                    >
                        {translate('assignAgentsSubtitle')}
                    </Typography>
                </Stack>
                <IconButton
                    onClick={onClose}
                    edge="end"
                    size="small"
                    aria-label={translate('close')}
                    sx={{ mt: 0.25, flexShrink: 0 }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    bgcolor: 'rgba(248, 250, 252, 0.55)',
                }}
            >
                <AssignAgentsList
                    sections={sections}
                    hasApps={appIds.length > 0}
                    loading={isLoading}
                    assigningRole={assigningRole}
                    onAssign={handleAssign}
                />
            </DialogContent>
        </Dialog>
    );
};

const AssignAgentsModal = ({ appIds, onClose, refreshMode = 'app' }: AssignAgentsModalProps) => {
    return (
        <ThemeProvider theme={appThemeV2}>
            <AssignAgentsDialog appIds={appIds} onClose={onClose} refreshMode={refreshMode} />
        </ThemeProvider>
    );
};

export default AssignAgentsModal;
